import { createAuthClient } from "better-auth/react";
import { inferAdditionalFields, phoneNumberClient } from "better-auth/client/plugins";

/**
 * Better Auth client configuration for the React Admin Panel
 *
 * This client handles authentication with the Better Auth backend,
 * including session management, login/logout, and token handling.
 *
 * IMPORTANT: baseURL should point to where Better Auth routes are mounted.
 * Backend routes are at: /api/auth/*
 * So baseURL should be: http://localhost:5000/api/auth
 */
export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_API_BASE_URL
    ? `${import.meta.env.VITE_API_BASE_URL}/auth`
    : "http://localhost:5000/api/auth",
  credentials: "include",
  plugins: [
    phoneNumberClient(),
    // Types session.user. Mirrors user.additionalFields in the backend's
    // better-auth.service.ts (separate repo, so its types can't be imported);
    // `role` is the admin plugin's column, which the backend's userRole also maps to.
    inferAdditionalFields({
      user: {
        firstName: { type: "string", required: true },
        lastName: { type: "string", required: true },
        username: { type: "string", required: false },
        gender: { type: "string", required: false },
        role: { type: "string", required: false },
        acceptedTermsVersion: { type: "string", required: false },
        acceptedPrivacyPolicyVersion: { type: "number", required: false },
      },
    }),
  ],
});

/** The app routes by role, so a session counts as signed in only with an email and a role. */
export const hasAppRole = <U extends { email?: string | null; role?: string | null }>(
  user: U | null | undefined,
): user is U & { email: string; role: string } => !!user?.email && !!user.role;

// Export specific methods for convenience
export const {
  signIn,
  signUp,
  signOut,
  useSession,
  getSession,
} = authClient;
